import { useCallback, useRef } from 'react';

const DEFAULT_DEBOUNCE_TIMEOUT_MS = 250;

/**
 * Creates a debounced version of a callback function.
 * Delays execution until no new calls arrive within the specified delay.
 * Uses trailing-edge: fires after the delay elapses, not on the first call.
 *
 * @param callback - The function to debounce
 * @param delayMs - Delay in milliseconds after the last call before executing
 * @returns Debounced callback function
 *
 * @example
 * const debouncedSearch = useDebouncedCallback((query: string) => {
 *   search(query);
 * }, 300);
 */
export function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delayMs: number = DEFAULT_DEBOUNCE_TIMEOUT_MS
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        callbackRef.current(...args);
      }, delayMs);
    }) as T,
    [delayMs]
  );
}
